import React from "react";

import { Button, Center, Code, Container, Stack, Text, Title } from "@mantine/core";
import { IconBug } from "@tabler/icons-react";
import { saveAs } from "file-saver";
import { Helmet } from "react-helmet";

import { useAppState } from "../../hooks/useAppState";
import { ReduxState } from "../../lib/state/store";

type DiagnosticData = {
  reduxState: ReduxState;
  localStorage: Record<string, unknown>;
};

function useDiagnosticData(): () => DiagnosticData {
  const appState = useAppState((state) => state);
  const localStorage = window.localStorage;

  return React.useCallback(() => {
    return {
      reduxState: appState,
      localStorage: localStorage,
    };
  }, [appState, localStorage]);
}

function useDiagnosticReport(): () => Blob {
  const generateDiagnosticData = useDiagnosticData();

  return React.useCallback(() => {
    const diagnosticData = generateDiagnosticData();
    return new Blob([JSON.stringify(diagnosticData, null, 2)], {
      type: "application/json",
    });
  }, [generateDiagnosticData]);
}

export const ReportABugPage = (): JSX.Element => {
  const generateDiagnosticReport = useDiagnosticReport();
  const reportCallback = React.useCallback(() => {
    const report = generateDiagnosticReport();
    saveAs(report, "fs-checklist-studio-debug.json");
  }, [generateDiagnosticReport]);

  return (
    <Container size="md" py="md" style={{ height: "100%" }}>
      <Helmet>
        <title>Report a bug</title>
      </Helmet>
      <Stack>
        <Title order={1}>Report a bug</Title>
        <Stack spacing={50}>
          <Stack spacing={0}>
            <Text>
              Found something that doesn't quite work right? Click on the button below to generate a
              diagnostics package:
            </Text>
            <Stack justify="center" spacing="xl">
              <Center>
                <Button
                  onClick={() => reportCallback()}
                  variant="gradient"
                  size="md"
                  radius="xl"
                  mt="lg"
                  leftIcon={<IconBug />}
                >
                  Generate Diagnostics Package
                </Button>
              </Center>
            </Stack>
          </Stack>
          <Stack>
            <Text>Please email the diagnostics package and with a description of the bug to:</Text>
            <Text>
              <Center style={{ alignItems: "baseline" }}>
                <Code fz="lg" fw={600}>
                  bugs
                </Code>{" "}
                (at){" "}
                <Code fz="lg" fw={600}>
                  fs-checklist.studio
                </Code>
              </Center>
            </Text>
            <Text></Text>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};
